import {
  Box,
  Center,
  Flex,
  FormControl,
  HStack,
  Tag,
  useRadio,
  type UseRadioProps,
  VStack
} from '@chakra-ui/react'
import {
  Currency,
  type PostNordCheckoutDeliveryOptionSelected,
  type PostNordDeliveryOptionDetails,
  type PostNordDeliveryOptionFE
} from 'ecosystem'
import { GrandImage, GrandSelect, type GrandSelectSimpleOption } from 'ui'
import { roundNumber } from 'shared-utils'
import { type ChangeEvent, useMemo, useState } from 'react'
import { overrideText } from 'ui/lib/overrides'
import { useOverridesContext } from 'ui/lib/overrides/hooks'
import { type CurrencyOverrides } from '../../../types'
import nordicSwanLogo from './images/nordic_swan_logo.svg'
import postnordLogo from './images/postnord_logo.svg'
import { PostNordFormFieldLabel } from './PostNordFormField'
import { type PostNordOverrides } from './translations'

type SelectOption = GrandSelectSimpleOption<PostNordDeliveryOptionDetails>

interface PostNordDeliveryOptionProps {
  item: PostNordDeliveryOptionFE
  radioProps: UseRadioProps
  onChange: (item: PostNordCheckoutDeliveryOptionSelected) => void
}

export const PostNordDeliveryOptionCard = ({
  item,
  radioProps,
  onChange
}: PostNordDeliveryOptionProps) => {
  const overrides = useOverridesContext<keyof (PostNordOverrides & CurrencyOverrides)>()
  const {
    getInputProps,
    getRadioProps,
    getLabelProps,
    state: { isChecked }
  } = useRadio(radioProps)
  const inputProps = getInputProps()

  const isSubOptions = !!item.additionalOptions?.length

  const firstAdditionalOption = item.additionalOptions?.[0]

  const title = item.defaultOption
    ? item.defaultOption?.descriptiveTexts.checkout.title
    : firstAdditionalOption?.descriptiveTexts.checkout.title

  const briefDescription = item.defaultOption
    ? item.defaultOption?.descriptiveTexts.checkout.briefDescription
    : firstAdditionalOption?.descriptiveTexts.checkout.briefDescription

  const friendlyDeliveryInfo = item.defaultOption
    ? item.defaultOption?.descriptiveTexts.checkout.friendlyDeliveryInfo
    : `${firstAdditionalOption?.deliveryTime.dayRange.days} ${overrideText('dagar', overrides?.days)}`

  const fossilFree = item.defaultOption
    ? item.defaultOption?.sustainability?.fossilFree
    : firstAdditionalOption?.sustainability?.fossilFree

  const ecoLabel = item.defaultOption
    ? item.defaultOption?.sustainability?.nordicSwanEcoLabel
    : firstAdditionalOption?.sustainability?.nordicSwanEcoLabel

  const selectSubOptions = useMemo<SelectOption[]>(() => {
    return (
      item.additionalOptions?.map((option) => ({
        label: `${option.location?.name} - ${roundNumber((option.location?.distanceFromRecipientAddress ?? 0) / 1000, 2)} km`,
        value: option
      })) ?? []
    )
  }, [item.additionalOptions])

  const price = `${item.price} ${overrideText(Currency.SEK, overrides?.currency)}`

  const [selectedSubOption, setSelectedSubOption] = useState<PostNordDeliveryOptionDetails | null>(
    () => (item.additionalOptions?.length ? item.additionalOptions[0] : null)
  )

  const onSelectChange = (newSelectedItem: PostNordDeliveryOptionDetails | null) => {
    setSelectedSubOption(newSelectedItem)

    if (isChecked && newSelectedItem) {
      onChange({
        id: item.id,
        type: item.type,
        details: newSelectedItem,
        price: item.price
      })
    }
  }

  const onRadioChange = (e: ChangeEvent<HTMLInputElement>) => {
    if (e.target.checked) {
      if (selectedSubOption) {
        onChange({
          id: item.id,
          type: item.type,
          details: selectedSubOption,
          price: item.price
        })
      } else if (item.defaultOption) {
        onChange({
          id: item.id,
          type: item.type,
          details: item.defaultOption,
          price: item.price
        })
      }
    }
  }

  return (
    <VStack
      alignItems="stretch"
      w="100%"
      gap={4}
      borderWidth="1px"
      borderRadius="md"
      _checked={{
        bg: 'blue.50',
        borderColor: 'blue.500'
      }}
      p={4}
      tabIndex={0}
      {...(isSubOptions
        ? {}
        : {
            as: 'label',
            cursor: 'pointer',
            ...getLabelProps()
          })}
      {...getRadioProps()}
    >
      <input
        {...inputProps}
        onChange={(e) => {
          onRadioChange(e)
          inputProps?.onChange?.(e)
        }}
      />

      <VStack
        alignItems="stretch"
        w="100%"
        gap={4}
        {...(isSubOptions
          ? {
              as: 'label',
              cursor: 'pointer',
              ...getLabelProps({ htmlFor: inputProps.id })
            }
          : {})}
      >
        <HStack justify="space-between">
          <HStack>
            <Center
              w="6"
              h="6"
              borderRadius="full"
              borderWidth="1px"
              borderColor="blue.500"
              _before={
                isChecked
                  ? {
                      content: '""',
                      display: 'block',
                      w: '3',
                      height: '3',
                      bg: 'blue.500',
                      borderRadius: 'full'
                    }
                  : {}
              }
            />
            <Box fontWeight="bold">{`${title} - ${price}`}</Box>
          </HStack>
          <GrandImage src={postnordLogo} alt="PostNord" maxW="16" />
        </HStack>

        {friendlyDeliveryInfo && (
          <Box fontWeight="medium" fontSize="sm">
            {friendlyDeliveryInfo}
          </Box>
        )}

        {(ecoLabel || fossilFree) && (
          <Flex align="center" gap={2} flexWrap="wrap">
            {fossilFree && (
              <Tag colorScheme="green" size="lg" variant="outline" py={2}>
                Fossil-free delivery
              </Tag>
            )}

            {ecoLabel && (
              <Tag as={HStack} colorScheme="green" size="lg" variant="outline" py={2}>
                <GrandImage src={nordicSwanLogo} alt="Nordic Swan Ecolabel" maxW="4" />
                <Box>Nordic Swan Ecolabelled delivery</Box>
              </Tag>
            )}
          </Flex>
        )}

        {!!briefDescription?.length && (
          <Box fontSize="sm" color="text.mild">
            {briefDescription}
          </Box>
        )}
      </VStack>

      {!!selectSubOptions.length && (
        <FormControl>
          <PostNordFormFieldLabel>{`${overrides?.pickUp}:`}</PostNordFormFieldLabel>

          <GrandSelect<SelectOption, false>
            instanceId={`postnord-option-select-${item.id}`}
            options={selectSubOptions}
            defaultValue={selectSubOptions[0]}
            onChange={(newValue) => {
              onSelectChange(newValue?.value ?? null)
            }}
            menuPosition="fixed"
            isDisabled={false}
            styles={{
              container: (base) => ({
                ...base,
                width: '100%'
              })
            }}
          />
        </FormControl>
      )}
    </VStack>
  )
}
